import { createKeyedSignal } from '@react-rxjs/utils'
import { Dispatch, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { filter, fromEvent, map, of, startWith, withLatestFrom } from 'rxjs'

import { localOrFallbackStorage, sessionOrFallbackStorage } from '../utils/storage'

export type UseLocalStorage = [value: string, setItem: Dispatch<string>, clearItem: () => void]
const isSSR = typeof window === 'undefined'
const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect

const [localStorageState$, setLocalStorageState] = createKeyedSignal<string, string | null>()
const [sessionStorageState$, setSessionStorageState] = createKeyedSignal<string, string | null>()

function subscribeToStorageChanges({
  key,
  storage,
  updateComponentState,
  getComponentState,
}: {
  key: string
  storage: {
    getItem(key: string): string | null
    setItem(key: string, value: string): void
    removeItem(key: string): void
  }
  updateComponentState: (newValue: string | null) => void
  getComponentState: () => string
}) {
  // Choose the proper state signal based on storage type
  const state$ = storage === sessionOrFallbackStorage ? sessionStorageState$ : localStorageState$
  const setState =
    storage === sessionOrFallbackStorage ? setSessionStorageState : setLocalStorageState

  const storageEvent = isSSR
    ? of()
    : fromEvent<StorageEvent>(window, 'storage').pipe(
        filter((event) =>
          storage === sessionOrFallbackStorage
            ? event.storageArea === window.sessionStorage
            : event.storageArea === window.localStorage,
        ),
      )

  const subscriptions = [
    storageEvent
      .pipe(
        filter((event): event is StorageEvent & { key: string } => event.key === key),
        filter((event) => event.newValue !== event.oldValue),
        withLatestFrom(state$(key).pipe(startWith(storage.getItem(key)))),
        filter(([event, lastState]) => event.newValue !== lastState),
        map(([event]) => event),
      )
      .subscribe((event) => setState(event.key, event.newValue || '')),

    state$(key)
      .pipe(filter((newValue) => storage.getItem(key) !== newValue))
      .subscribe((newValue) =>
        newValue === null ? storage.removeItem(key) : storage.setItem(key, newValue),
      ),

    state$(key)
      .pipe(filter((newValue) => getComponentState() !== newValue))
      .subscribe((newValue) => updateComponentState(newValue || '')),
  ]
  return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
}

export type UseStorageParams = {
  key: string
  defaultValue?: string
  type?: 'local' | 'session'
}

export const useStorage = ({
  key,
  defaultValue = '',
  type = 'local',
}: UseStorageParams): UseLocalStorage => {
  const storage = type === 'session' ? sessionOrFallbackStorage : localOrFallbackStorage
  const setStorageState = type === 'session' ? setSessionStorageState : setLocalStorageState

  const storedValue = storage.getItem(key) || ''
  const [value, setValue] = useState<string>(storedValue || defaultValue)

  const clearItem = useCallback(() => setStorageState(key, null), [key, setStorageState])
  const setItem = useCallback(
    (newValue: string) => setStorageState(key, newValue),
    [key, setStorageState],
  )

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = subscribeToStorageChanges({
      key,
      storage,
      updateComponentState: (newValue) => setValue(newValue || ''),
      getComponentState: () => value,
    })
    return unsubscribe
  }, [key, value, storage])

  return [value, setItem, clearItem]
}
